import { SelectSearch } from 'cwp-components/forms/SelectSearch'
import { useLanguages } from 'hooks/useLanguages'
import { useSearchFilters } from 'hooks/useSearchFilters'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

type ParamUpdate = [string, string[]]

export const DropdownFilters = () => {
  const [activeDropdownId, setActiveDropdownId] = useState<string | null>(null)

  const { dropdownLanguages } = useLanguages()
  const { lawAreas, lawSubAreas, cities, legalServices } = useSearchFilters()
  const [searchParams, setSearchParams] = useSearchParams()

  const getParamArray = (key: string): string[] =>
    searchParams.get(key)?.split(',') ?? []

  const setParamArray = (
    keyOrEntries: string | ParamUpdate[],
    values?: string[],
  ) => {
    const newParams = new URLSearchParams(searchParams.toString())

    const updates: ParamUpdate[] =
      typeof keyOrEntries === 'string'
        ? [[keyOrEntries, values ?? []]]
        : keyOrEntries

    updates.forEach(([key, vals]) => {
      if (vals.length) {
        newParams.set(key, vals.join(','))
      } else {
        newParams.delete(key)
      }
    })
    setSearchParams(newParams)
  }
  const toggle = (id: string) =>
    setActiveDropdownId((prev) => (prev === id ? null : id))

  const commonProps = {
    withSearch: true,
  }

  const filters = [
    {
      id: 'Language',
      options: dropdownLanguages,
      value: getParamArray('language_ids'),
      onSelect: (value: string[]) => setParamArray('language_ids', value),
    },
    {
      id: 'City',
      options: cities,
      value: getParamArray('city'),
      onSelect: (value: string[]) => setParamArray('city', value),
      isLoading: cities.length === 0,
    },
    {
      id: 'MeetingType',
      options: [
        { label: 'In person', value: 'IN_PERSON' },
        { label: 'Remote', value: 'REMOTE' },
      ],
      value: getParamArray('meeting_type'),
      onSelect: (value: string[]) => setParamArray('meeting_type', value),
    },
    {
      id: 'AreaOfLaw',
      options: lawAreas,
      value: getParamArray('area_ids'),
      multiple: true,
      onSelect: (areaIds: string[]) => {
        if (areaIds.length === 0) {
          setParamArray([
            ['subarea_ids', []],
            ['area_ids', []],
          ])
        } else {
          setParamArray('area_ids', areaIds)
        }
      },
      isLoading: lawAreas.length === 0,
    },
    {
      id: 'SubAreaOfLaw',
      options: lawSubAreas,
      value: getParamArray('subarea_ids'),
      multiple: true,
      cap: 10,
      limit: 3,
      onSelect: (selectedIds: string[]) => {
        const fromSelected = lawSubAreas
          .filter((sub) => selectedIds.includes(sub.value))
          .map((sub) => sub.area_id?.toString())
          .filter((id): id is string => !!id)

        const currentAreaIds = getParamArray('area_ids')
        const merged = Array.from(new Set([...currentAreaIds, ...fromSelected]))

        setParamArray([
          ['subarea_ids', selectedIds],
          ['area_ids', merged],
        ])
      },
      isLoading: lawSubAreas.length === 0,
    },
    {
      id: 'LegalServices',
      options: legalServices,
      value: getParamArray('legal_service_ids'),
      multiple: true,
      cap: 10,
      onSelect: (value: string[]) => setParamArray('legal_service_ids', value),
      isLoading: lawSubAreas.length === 0,
    },
    {
      id: 'ProfileType',
      options: [
        { value: 'solicitor', label: 'Solicitor' },
        { value: 'barrister', label: 'Barrister' },
      ],
      value: getParamArray('profile_type'),
      onSelect: (value: string[]) => setParamArray('profile_type', value),
    },
  ]

  return (
    <div className='flex flex-col space-y-4 w-full md:w-auto'>
      {filters.map((filter) => (
        <SelectSearch
          key={filter.id}
          id={filter.id}
          options={filter.options}
          placeholder={filter.id.replace(/([A-Z])/g, ' $1').trim()}
          isOpen={activeDropdownId === filter.id}
          onToggle={() => toggle(filter.id)}
          value={filter.value}
          onSelect={filter.onSelect}
          multiple={filter.multiple}
          cap={filter.cap}
          limit={filter.limit}
          isLoading={filter.isLoading}
          withSearch={commonProps.withSearch}
        />
      ))}
    </div>
  )
}
